import {OperationTypes} from '@jimmyn/apollo-client';
import {Button, Modal, Space} from 'antd';
import {
  PauseSubscriptionInput,
  SubscriptionFieldsFragment,
  usePauseSubscriptionMutation
} from 'api/generated';
import {subscriptionQuery} from 'api/queries';
import {Formik} from 'formik';
import {Form, FormItem, Input, Radio} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {formatUnixDate} from 'lib/formaters';
import {getIntervalLabel} from 'lib/subscriptionUtils';
import moment from 'moment-timezone';
import React, {useState} from 'react';
import * as yup from 'yup';

type Props = {subscription: SubscriptionFieldsFragment};

const validationSchema = yup.object().shape({
  pauseIntervalCount: yup.number().min(1).max(999).required()
});

export const PauseSubscription: React.FC<Props> = ({subscription}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pauseSubscription] = usePauseSubscriptionMutation({
    updateQuery: {query: subscriptionQuery, variables: {subscriptionId: subscription?.id}},
    operationType: OperationTypes.UPDATE
  });

  const initialValues: PauseSubscriptionInput = {
    id: subscription.id,
    accountId: subscription.accountId,
    pauseIntervalCount: 1,
    pauseAtPeriodEnd: true
  };

  const handlePause = async (
    input: PauseSubscriptionInput,
    {setSubmitting}: FormikHelpers<PauseSubscriptionInput>
  ) => {
    try {
      await pauseSubscription({variables: {input}});
      setIsOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const intervalLabel = getIntervalLabel(subscription);

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>Pause</Button>
      <Formik
        initialValues={initialValues}
        onSubmit={handlePause}
        validationSchema={validationSchema}>
        {({submitForm, isSubmitting, resetForm, values}) => {
          const intervalCount = values.pauseIntervalCount * subscription.intervalCount;
          // i18next-extract-disable-next-line
          const interval = subscription.interval;
          const resumeDate = moment
            .unix(subscription.nextPaymentAt!)
            .utc()
            .add(intervalCount, subscription.interval)
            .unix();
          return (
            <Modal
              title="Pause subscription"
              okText="Pause subscription"
              cancelText="Never mind"
              open={isOpen}
              confirmLoading={isSubmitting}
              onOk={submitForm}
              onCancel={() => setIsOpen(false)}
              afterClose={resetForm}>
              <Form layout="vertical">
                <FormItem name="pauseAtPeriodEnd">
                  <Radio.Group name="pauseAtPeriodEnd">
                    <Space direction="vertical">
                      <Radio value={true} name="pauseAtPeriodEnd">
                        Pause at the current period end (
                        {formatUnixDate(subscription.currentPeriodEnd)})
                      </Radio>
                      <Radio value={false} name="pauseAtPeriodEnd">
                        Pause immediately
                      </Radio>
                    </Space>
                  </Radio.Group>
                </FormItem>
                <FormItem name="pauseIntervalCount" label="Pause for">
                  <Input name="pauseIntervalCount" type="number" suffix={`× ${intervalLabel}`} />
                </FormItem>
                <div>
                  Subscription will be automatically reactivated after {intervalCount} {interval} on{' '}
                  <b>{formatUnixDate(resumeDate)}</b>
                </div>
                <input type="submit" value="submit" hidden />
              </Form>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
